import React, { useEffect, useState } from 'react';
import { message, Button } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import {
    GET_DASHBOARD_REPORT_FILE_LISTING,
    DashboardReportListResponse,
} from './graphql/queries/getDashboardReportFileListing';
import {
    GET_DASHBOARD_BATCH_REPORT_DOWNLOAD_URLS,
    GetDashboardBatchReportDownloadUrlsResponse,
    GetDashboardBatchReportDownloadUrlsVariables,
} from './graphql/queries/getDashboardBatchReportDownloadUrls';
import CentralReportFileFilter from '../../central/download/components/CentralReportFileFilter';
import CentralReportFileColumns from '../../central/download/components/CentralReportFileColumns';
import { ReportFileFiltersType } from '../../central/download/hooks/useCentralReportFileQuery';
import { ReportFileType } from '../../central/download/graphql/queries/getReportFileListingForCentral';
import TablePaginationBar from '../../../components/TablePaginationBar';
import { formatTimeRangeToISO, getLastWeekDateTimeRange } from '../../../utils/dateTimeUtils';
import { getUserInfo } from '../../../utils/auth';

const [defaultStart, defaultEnd] = getLastWeekDateTimeRange();

const getInitialFilters = (): ReportFileFiltersType => {
    const userInfo = getUserInfo();
    return {
        type: undefined,
        name: undefined,
        reportDate: [defaultStart, defaultEnd],
        operatorId: userInfo?.account.id ? parseInt(userInfo.account.id, 10) : undefined,
    };
};

const DashboardReportFilePage: React.FC = () => {
    const { t } = useTranslation();
    const userInfo = getUserInfo();
    const currentUserId = userInfo?.account.id ? parseInt(userInfo.account.id, 10) : undefined;
    const currentUserName = userInfo?.account.name ?? '';
    const operatorOptions = currentUserId
        ? [{ value: String(currentUserId), label: currentUserName }]
        : [];
    const [tempFilters, setTempFilters] = useState<ReportFileFiltersType>(getInitialFilters);
    const [filters, setFilters] = useState<ReportFileFiltersType>(getInitialFilters);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [reportFileData, setReportFileData] = useState<ReportFileType[]>([]);
    const [totalElements, setTotalElements] = useState(0);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportFileType[]>([]);
    const [downloadLoading, setDownloadLoading] = useState(false);

    const buildQueryVariables = () => ({
        filters: {
            ...filters,
            reportDate: formatTimeRangeToISO(filters.reportDate),
        },
        pageRequest: {
            page: currentPage,
            size: pageSize,
            sortBy: 'createdAt',
            sortDirection: 'DESC',
        },
    });

    const { data, loading, refetch } = useQuery<DashboardReportListResponse>(
        GET_DASHBOARD_REPORT_FILE_LISTING,
        { variables: buildQueryVariables() }
    );

    const [getBatchDownloadUrls] = useLazyQuery<
        GetDashboardBatchReportDownloadUrlsResponse,
        GetDashboardBatchReportDownloadUrlsVariables
    >(GET_DASHBOARD_BATCH_REPORT_DOWNLOAD_URLS);

    useEffect(() => {
        if (data?.getDashboardReportFileListing) {
            setReportFileData(data.getDashboardReportFileListing.content as unknown as ReportFileType[]);
            setTotalElements(
                parseInt(data.getDashboardReportFileListing.totalElements.toString()) || 0
            );
        }
    }, [data]);

    useEffect(() => {
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize]);

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: ReportFileType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    const handleResetFilters = () => {
        const reset = getInitialFilters();
        setTempFilters(reset);
        setFilters(reset);
        setCurrentPage(1);
    };

    const handlePageChange = (page: number, size: number) => {
        setCurrentPage(page);
        setPageSize(size);
    };

    const generateFilename = (index: number, reportName?: string) => {
        const timestamp = new Date().toISOString().split('T')[0];
        if (reportName) {
            if (/\.(xlsx|xls|pdf|zip|csv|doc|docx|txt|bmp|jpe?g|png)$/i.test(reportName)) {
                return reportName;
            }
            return `${reportName}.xlsx`;
        }
        return `Report_${index + 1}_${timestamp}.xlsx`;
    };

    const downloadFile = async (url: string, filename: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);
    };

    const handleBatchDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning('Please select the report you wish to download');
            return;
        }
        setDownloadLoading(true);
        try {
            const reportIds = selectedRows.map(report => parseInt(report.id.toString(), 10));
            const { data: urlData } = await getBatchDownloadUrls({
                variables: { reportIds },
                fetchPolicy: 'network-only',
            });
            if (!urlData?.getDashboardBatchReportDownloadUrls) {
                message.error('Failed to obtain the download link.');
                return;
            }
            const downloadUrls = urlData.getDashboardBatchReportDownloadUrls;
            if (downloadUrls.length === 0) {
                message.warning('No available download link is available.');
                return;
            }
            if (downloadUrls.length > 1) {
                message.info(`Starting batch download of ${downloadUrls.length} files...`);
            }
            for (let i = 0; i < downloadUrls.length; i++) {
                const url = downloadUrls[i];
                const reportName = i < selectedRows.length ? selectedRows[i].name : undefined;
                const filename = generateFilename(i, reportName);
                try {
                    await downloadFile(url, filename);
                    await new Promise(res => setTimeout(res, 500));
                    message.success(`File ${i + 1} downloaded`);
                } catch {
                    message.error(`Failed to download file ${i + 1}`);
                }
            }
            message.success(`All ${downloadUrls.length} files download initiated successfully!`);
        } catch {
            message.error('Batch download failed, please try again later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="p-6">
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
            <CentralReportFileFilter
                filters={tempFilters}
                setFilters={setTempFilters}
                operatorOptions={operatorOptions}
            />

            <div className="flex justify-end items-center gap-2 mb-3 pl-0">
                <Button
                    type="primary"
                    onClick={handleFilter}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.download.filters.filter')}</span>
                </Button>
                <Button
                    type="primary"
                    onClick={handleBatchDownload}
                    loading={downloadLoading}
                    disabled={selectedRows.length === 0}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.download.filters.batchDownload')} ({selectedRows.length})</span>
                </Button>
                <Button
                    onClick={handleResetFilters}
                    className="w-auto min-w-24"
                >
                    <span className="font-bold">{t('central.download.filters.reset')}</span>
                </Button>
            </div>

            <CentralReportFileColumns
                reportFileData={reportFileData}
                onSelectChange={handleSelectChange}
                selectedRowKeys={selectedRowKeys}
                loading={loading}
            />

            <TablePaginationBar
                total={totalElements}
                current={currentPage}
                pageSize={pageSize}
                onChange={handlePageChange}
                showQuickJumper={false}
            />
        </div>
        </div>
    );
};

export default DashboardReportFilePage;
